<script setup lang="ts">
import { MenuEntry } from "@/models/menu-entry";
import { computed, h, PropType, VNode } from "vue";

defineOptions({ name: "BcMenuEntry", inheritAttrs: false });

const props = defineProps({
  item: { type: Object as PropType<MenuEntry>, required: true },
  subItemIcon: {
    type: Object as PropType<VNode>,
    default: h("svg", { class: "ionicon", viewBox: "0 0 512 512" }, [
      h("path", {
        fill: "none",
        stroke: "currentColor",
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        "stroke-width": "48",
        d: "M184 112l144 144-144 144",
      }),
    ]),
  },
  hasSeparator: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  textWrap: { type: Boolean, default: false },
  hasIconPlaceHolder: { type: Boolean, default: false },
});

const emit = defineEmits<{
  (e: "click", payload: MenuEntry): void;
  (e: "mouseenter"): void;
}>();

const hasSubItemIcon = computed(() => {
  return props.item.subItems && props.item.subItems.length && props.subItemIcon;
});

function onClick(event: MouseEvent) {
  if (props.item.disabled) return;

  event.stopImmediatePropagation();
  if (props.item.callback) {
    props.item.callback(event);
  }
  emit("click", props.item);
}
</script>

<template>
  <component v-if="item.component" :is="item.component" />
  <div
    v-else
    class="menuItem"
    :class="{
      selected: selected,
      disabled: item.disabled,
      textWrap: textWrap,
    }"
    @click.prevent="onClick($event)"
    @contextmenu.prevent="onClick($event)"
    @mouseenter="emit('mouseenter')"
  >
    <div class="menuItemWrapper">
      <slot name="before-item">
        <component class="menuItemIcon" v-if="item.icon" :is="item.icon" />
      </slot>
      <div class="menuItemText" :class="{ noIcon: hasIconPlaceHolder && !item.icon }">
        {{ item.text }}
      </div>
      <component
        class="menuItemIcon menuSubItemIcon"
        v-if="hasSubItemIcon"
        :is="props.subItemIcon"
      />
    </div>

    <slot name="subMenu" />
  </div>

  <div class="menuItemSeparator" v-if="hasSeparator" />
</template>

<style lang="less">
@import "../../styles/components/menu/bc-menu-entry.less";
</style>
